// ============================================================
// FILE: src/components/FloatingWidgets.jsx
// The 3 floating elements on every page:
//   1. WhatsApp chat button (bottom-right, green)
//   2. Back-to-top button (bottom-left)
//   3. Cookie consent banner
// WhatsApp number is in src/data/site.js (CONTACT DETAILS).
// ============================================================

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, MessageCircle, X, Cookie } from "lucide-react";
import { CONTACT_INFO } from "../data/site";

export function WhatsAppFloat() {
  return (
    <motion.a
      href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent("Hello Evolve Trade & Logistics, I'd like to discuss a trade inquiry.")}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat on WhatsApp"
      data-testid="whatsapp-float-button"
      className="fixed bottom-6 right-6 z-[95] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-[0_12px_40px_-8px_rgba(20,184,166,0.6)]"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 2.4, type: "spring", stiffness: 200, damping: 15 }}
      whileHover={{ scale: 1.12 }}
      whileTap={{ scale: 0.94 }}
    >
      <MessageCircle className="h-6 w-6" />
      <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-teal-500/30" />
    </motion.a>
  );
}

export function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.button
          onClick={() => window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
          data-testid="back-to-top-button"
          className="glass fixed bottom-6 left-6 z-[95] flex h-11 w-11 items-center justify-center rounded-full text-foreground transition-colors hover:text-teal-500"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          whileHover={{ scale: 1.1 }}
        >
          <ArrowUp className="h-4 w-4" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

export function CookieBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("evolve-cookies")) {
      const t = setTimeout(() => setShow(true), 2600);
      return () => clearTimeout(t);
    }
  }, []);
  const accept = () => {
    localStorage.setItem("evolve-cookies", "accepted");
    setShow(false);
  };
  return (
    <AnimatePresence>
      {show && (
        <div className="fixed bottom-4 left-4 right-4 z-[96] sm:bottom-6 sm:left-1/2 sm:right-auto sm:w-full sm:max-w-lg sm:-translate-x-1/2">
          <motion.div
            data-testid="cookie-banner"
            className="glass rounded-2xl p-5 shadow-2xl"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
          <div className="flex items-start gap-4">
            <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-teal-500" />
            <div className="flex-1">
              <p className="text-sm font-semibold">We value your privacy</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                We use cookies to analyse traffic and improve your experience. By continuing, you consent to our use of cookies.
              </p>
            </div>
            <button onClick={accept} className="btn-primary !px-5 !py-2 !text-xs" data-testid="cookie-accept-button">Accept</button>
            <button onClick={accept} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground" data-testid="cookie-dismiss-button">
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
